import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';
import { CategoryDialog } from './CategoryDialog';
import { CategoryTransactionsDialog } from './CategoryTransactionsDialog';

type Category = {
  id: string;
  name: string;
  paymentMethod: string;
  paymentSource: string | null;
  isRecurring: boolean;
  isVariable: boolean;
  memberId: string | null;
  memberDisplayName: string | null;
  sortOrder: number;
};

type Group = {
  id: string;
  name: string;
  sortOrder: number;
  categories: Category[];
};

export function CategoryList() {
  const { confirm } = useConfirm();
  const query = trpc.category.listGroups.useQuery();
  const utils = trpc.useUtils();
  const deleteMutation = trpc.category.delete.useMutation({
    onMutate: async ({ id }) => {
      await utils.category.listGroups.cancel();
      const previous = utils.category.listGroups.getData();
      utils.category.listGroups.setData(undefined, (old) => {
        if (!old) return old;
        return (old as Group[]).map((g) => ({
          ...g,
          categories: g.categories.filter((c) => c.id !== id),
        }));
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        utils.category.listGroups.setData(undefined, context.previous);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      void utils.category.listGroups.invalidate();
    },
    onSuccess: () => {
      toast.success('Kategori borttagen');
    },
  });
  const _membersQuery = trpc.income.list.useQuery();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [viewingCategory, setViewingCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);

  if (query.isLoading) return <TableSkeleton rows={5} columns={4} />;
  if (query.error) return <p className="text-sm text-destructive">{query.error.message}</p>;

  const groups: Group[] = query.data ?? [];

  if (groups.length === 0) {
    return (
      <EmptyState
        icon="📂"
        title="Inga kategorier"
        description="Skapa kategorigrupper och kategorier för att organisera din budget."
      />
    );
  }

  const renderCategoryButton = (cat: Category, showRecurring: boolean) => (
    <button
      type="button"
      className="min-h-11 w-full text-left sm:min-h-0"
      onClick={() =>
        setViewingCategory({
          id: cat.id,
          name: cat.name,
        })
      }
    >
      <div className="break-words text-sm text-foreground hover:text-primary hover:underline">
        {cat.name}
      </div>
      <div className="text-xs text-muted-foreground/70">
        {cat.paymentMethod}
        {cat.paymentSource ? ` · ${cat.paymentSource}` : ''}
        {cat.memberDisplayName ? ` · ${cat.memberDisplayName}` : ''}
        {showRecurring && cat.isRecurring ? ' · återkommande' : ''}
      </div>
    </button>
  );

  const renderActions = (cat: Category, groupId: string) => (
    <div className="flex shrink-0 items-center justify-end gap-1 sm:gap-2">
      <Button
        type="button"
        onClick={() => {
          setEditingCategory(cat);
          setSelectedGroupId(groupId);
          setDialogOpen(true);
        }}
        variant="ghost"
        size="sm"
        aria-label={`Redigera ${cat.name}`}
        className="h-11 px-3 text-xs text-muted-foreground hover:text-foreground sm:h-auto sm:px-2"
      >
        Redigera
      </Button>
      <Button
        type="button"
        onClick={async () => {
          const ok = await confirm({
            title: 'Ta bort kategori',
            message: `Är du säker på att du vill ta bort "${cat.name}"?`,
            confirmLabel: 'Ta bort',
            destructive: true,
          });
          if (ok) deleteMutation.mutate({ id: cat.id });
        }}
        variant="ghost"
        size="sm"
        aria-label={`Ta bort ${cat.name}`}
        className="h-11 px-3 text-xs text-destructive hover:text-destructive/80 sm:h-auto sm:px-2"
      >
        Ta bort
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <Card key={group.id} className="overflow-hidden rounded-2xl py-0 max-sm:gap-0">
          <CardHeader className="flex flex-row items-center justify-between gap-3 border-b bg-muted/40 py-1 max-sm:[.border-b]:pb-1 sm:py-2.5">
            <CardTitle className="min-w-0 break-words text-[15px] font-semibold">
              {group.name}
            </CardTitle>
            <Button
              type="button"
              onClick={() => {
                setEditingCategory(null);
                setSelectedGroupId(group.id);
                setDialogOpen(true);
              }}
              variant="link"
              size="sm"
              aria-label={`Lägg till kategori i ${group.name}`}
              className="h-11 px-2 sm:h-auto sm:px-0"
            >
              + Lägg till
            </Button>
          </CardHeader>
          {group.categories.length === 0 ? (
            <CardContent className="px-4 py-3 text-sm text-muted-foreground/70">
              Inga kategorier ännu.
            </CardContent>
          ) : (
            <>
              {/* Mobile: stacked rows */}
              <ul className="divide-y divide-border md:hidden">
                {group.categories.map((cat) => (
                  <li key={cat.id} className="flex items-center gap-2 py-1 pl-4 pr-2">
                    <div className="min-w-0 flex-1">{renderCategoryButton(cat, true)}</div>
                    {renderActions(cat, group.id)}
                  </li>
                ))}
              </ul>

              {/* Desktop: table */}
              <table className="hidden w-full md:table">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-1.5 pl-4 text-left font-medium">Kategori</th>
                    <th className="py-1.5 px-2 text-center font-medium">
                      <abbr title="Återkommande" className="no-underline">
                        Återkomm.
                      </abbr>
                    </th>
                    <th className="py-1.5 pr-4 text-right font-medium">
                      <span className="sr-only">Åtgärder</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="py-2 pl-4 pr-2">{renderCategoryButton(cat, false)}</td>
                      <td className="py-2 px-2 text-center text-sm">
                        <span aria-hidden="true">{cat.isRecurring ? '✓' : '—'}</span>
                        <span className="sr-only">{cat.isRecurring ? 'Ja' : 'Nej'}</span>
                      </td>
                      <td className="py-2 pr-4 text-right">{renderActions(cat, group.id)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      ))}

      {dialogOpen && (
        <CategoryDialog
          groupId={selectedGroupId}
          groups={groups}
          category={editingCategory}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            void utils.category.listGroups.invalidate();
          }}
        />
      )}

      {viewingCategory && (
        <CategoryTransactionsDialog
          categoryId={viewingCategory.id}
          categoryName={viewingCategory.name}
          onClose={() => setViewingCategory(null)}
        />
      )}
    </div>
  );
}
